import type { ConflictZone, DroneZone, NuclearAsset, FighterJet } from '@/types';

// ---------------------------------------------------------------------------
// Conflict Zones — Based on ACLED, UCDP, and open-source intelligence
// Updated: March 2026
// ---------------------------------------------------------------------------

export const CONFLICT_ZONES: ConflictZone[] = [
  // ── Active Wars ──────────────────────────────────────────────────────────
  {
    id: 'cz-ua-1', name: 'Ukraine-Russia War (Eastern Front)',
    type: 'active-war', lat: 48.5, lon: 36.5, radiusKm: 180,
    severity: 'critical', parties: ['Russia', 'Ukraine'],
    startDate: '2022-02-24',
    description: 'Full-scale Russian invasion of Ukraine. Active frontlines in Zaporizhzhia, Donetsk, Kharkiv oblasts.',
    country: 'Ukraine',
  },
  {
    id: 'cz-ua-2', name: 'Ukraine-Russia War (Kherson)',
    type: 'active-war', lat: 46.6, lon: 32.6, radiusKm: 80,
    severity: 'critical', parties: ['Russia', 'Ukraine'],
    startDate: '2022-03-01',
    description: 'Occupied Kherson region, active shelling across Dnipro river.',
    country: 'Ukraine',
  },
  {
    id: 'cz-ua-3', name: 'Kursk Incursion Zone',
    type: 'active-war', lat: 51.7, lon: 35.4, radiusKm: 60,
    severity: 'high', parties: ['Ukraine', 'Russia', 'DPRK Troops'],
    startDate: '2024-08-06',
    description: 'Ukrainian cross-border operation into Russian Kursk Oblast. DPRK troops reportedly deployed.',
    country: 'Russia',
  },
  {
    id: 'cz-gz-1', name: 'Gaza Strip War',
    type: 'active-war', lat: 31.4, lon: 34.4, radiusKm: 25,
    severity: 'critical', parties: ['Israel', 'Hamas', 'Islamic Jihad'],
    startDate: '2023-10-07',
    description: 'Phase 1 ceasefire active since Jan 19, 2025. Phase 2 negotiations ongoing. Risk of collapse remains.',
    country: 'Palestine',
    status: 'ceasefire',
    ceasefireDate: '2025-01-19',
    ceasefireNotes: 'Phase 1 deal (6 weeks) brokered by Qatar/Egypt. Hostage releases ongoing. Phase 2 uncertain.',
  },
  {
    id: 'cz-lb-1', name: 'Lebanon-Israel Conflict',
    type: 'tension', lat: 33.3, lon: 35.5, radiusKm: 50,
    severity: 'medium', parties: ['Israel', 'Hezbollah', 'Lebanon'],
    startDate: '2023-10-08',
    description: 'Ceasefire since Nov 27, 2024. UNIFIL monitoring. IDF withdrawal ongoing, Hezbollah disarming south.',
    country: 'Lebanon',
    status: 'ceasefire',
    ceasefireDate: '2024-11-27',
    ceasefireNotes: 'US-France brokered deal. 60-day ceasefire extended. IDF completing staged withdrawal.',
  },
  {
    id: 'cz-sd-1', name: 'Sudan Civil War (Khartoum)',
    type: 'active-war', lat: 15.6, lon: 32.5, radiusKm: 80,
    severity: 'critical', parties: ['SAF', 'RSF (Rapid Support Forces)'],
    startDate: '2023-04-15',
    description: 'Fighting between Sudanese Armed Forces and RSF. Mass atrocities reported in Darfur.',
    country: 'Sudan',
  },
  {
    id: 'cz-sd-2', name: 'Sudan Civil War (Darfur)',
    type: 'active-war', lat: 13.5, lon: 25.0, radiusKm: 120,
    severity: 'critical', parties: ['SAF', 'RSF'],
    startDate: '2023-04-20',
    description: 'Darfur conflict with ethnic cleansing reports.',
    country: 'Sudan',
  },
  {
    id: 'cz-mm-1', name: 'Myanmar Civil War',
    type: 'active-war', lat: 21.0, lon: 96.5, radiusKm: 150,
    severity: 'high', parties: ['Myanmar Junta (SAC)', 'PDF', 'Ethnic Armed Organizations'],
    startDate: '2021-02-01',
    description: 'Armed resistance against military junta following 2021 coup. Multiple fronts across country.',
    country: 'Myanmar',
  },
  {
    id: 'cz-ye-1', name: 'Yemen Civil War / Houthi Operations',
    type: 'active-war', lat: 15.4, lon: 44.2, radiusKm: 150,
    severity: 'high', parties: ['Houthi (Ansar Allah)', 'Saudi-led Coalition', 'Yemen Govt'],
    startDate: '2014-09-21',
    description: 'Ongoing civil war. Houthi maritime attacks in Red Sea disrupting global trade.',
    country: 'Yemen',
  },
  {
    id: 'cz-sy-1', name: 'Syria Post-Assad Transition',
    type: 'tension', lat: 35.0, lon: 38.5, radiusKm: 200,
    severity: 'medium', parties: ['HTS (governing)', 'SDF/Kurds', 'Turkey', 'US forces remaining'],
    startDate: '2011-03-15',
    description: 'Assad regime fell Dec 8, 2024. HTS-led transitional government. Residual ISIS activity. Turkey-SDF tensions.',
    country: 'Syria',
    status: 'negotiating',
    ceasefireDate: '2024-12-08',
    ceasefireNotes: 'Assad fled to Russia Dec 8. HTS leading transition. Constitutional talks in progress.',
  },
  {
    id: 'cz-so-1', name: 'Somalia / Al-Shabaab',
    type: 'insurgency', lat: 5.5, lon: 46.2, radiusKm: 200,
    severity: 'high', parties: ['Al-Shabaab', 'Somali Federal Govt', 'AMISOM'],
    startDate: '2006-01-01',
    description: 'Al-Shabaab insurgency across central and southern Somalia.',
    country: 'Somalia',
  },
  {
    id: 'cz-ml-1', name: 'Mali / Sahel Insurgency',
    type: 'insurgency', lat: 16.0, lon: -3.0, radiusKm: 300,
    severity: 'high', parties: ['JNIM', 'ISGS', 'Mali Military (Russian Wagner group)'],
    startDate: '2012-01-01',
    description: 'Jihadist insurgency across central Mali. Wagner Group (Africa Corps) presence.',
    country: 'Mali',
  },
  {
    id: 'cz-et-1', name: 'Ethiopia (Amhara/Tigray)',
    type: 'insurgency', lat: 12.0, lon: 38.0, radiusKm: 150,
    severity: 'medium', parties: ['Ethiopian Govt', 'FANO', 'OLA'],
    startDate: '2023-01-01',
    description: 'Ongoing armed conflict in Amhara region after Tigray ceasefire.',
    country: 'Ethiopia',
  },
  {
    id: 'cz-iq-1', name: 'Iraq / ISIS Remnants',
    type: 'insurgency', lat: 34.5, lon: 43.0, radiusKm: 100,
    severity: 'medium', parties: ['ISIS', 'Iraqi Security Forces', 'PMF', 'US forces'],
    startDate: '2014-01-01',
    description: 'Residual ISIS activity in the Iraqi-Syrian desert.',
    country: 'Iraq',
  },
  // ── Tensions ─────────────────────────────────────────────────────────────
  {
    id: 'cz-tw-1', name: 'Taiwan Strait Tension',
    type: 'tension', lat: 24.0, lon: 120.5, radiusKm: 200,
    severity: 'high', parties: ['China', 'Taiwan', 'United States'],
    startDate: '2022-08-01',
    description: 'Elevated PLA military exercises and ADIZ incursions around Taiwan.',
    country: 'Taiwan',
  },
  {
    id: 'cz-kr-1', name: 'Korean Peninsula DMZ',
    type: 'tension', lat: 38.1, lon: 127.0, radiusKm: 80,
    severity: 'high', parties: ['North Korea', 'South Korea', 'United States'],
    startDate: '1950-07-27',
    description: 'DPRK missile tests and military provocations. US-ROK joint exercises ongoing.',
    country: 'Korea',
  },
  {
    id: 'cz-ir-1', name: 'Iran Nuclear Tensions',
    type: 'tension', lat: 32.5, lon: 53.0, radiusKm: 200,
    severity: 'high', parties: ['Iran', 'Israel', 'United States'],
    startDate: '2019-05-01',
    description: 'Iran enriching uranium to ~84%. Risk of Israeli preventive strike.',
    country: 'Iran',
  },
  {
    id: 'cz-scs-1', name: 'South China Sea Disputes',
    type: 'tension', lat: 12.5, lon: 114.5, radiusKm: 300,
    severity: 'high', parties: ['China', 'Philippines', 'Vietnam', 'Malaysia', 'US Navy'],
    startDate: '2012-04-01',
    description: 'Chinese coast guard and navy harassing Philippine resupply vessels at Ayungin Shoal.',
    country: 'Philippines',
  },
  {
    id: 'cz-pk-1', name: 'Pakistan-Afghanistan Border',
    type: 'insurgency', lat: 34.0, lon: 70.5, radiusKm: 120,
    severity: 'medium', parties: ['TTP (Pakistani Taliban)', 'Pakistan', 'Afghanistan (Taliban)'],
    startDate: '2021-08-15',
    description: 'TTP cross-border attacks from Afghanistan into Pakistan.',
    country: 'Pakistan',
  },
  // ── Persian Gulf / Middle East Escalation ────────────────────────────────
  {
    id: 'cz-gulf-ir-1', name: 'Iran–Gulf States Escalation',
    type: 'tension', lat: 25.2, lon: 55.3, radiusKm: 120,
    severity: 'critical', parties: ['Iran', 'UAE', 'Saudi Arabia', 'United States'],
    startDate: '2024-10-01',
    description: 'Escalating Iranian strikes and drone/missile threats targeting Gulf state infrastructure. UAE/Dubai on heightened alert.',
    country: 'United Arab Emirates',
  },
  {
    id: 'cz-gulf-hormuz-1', name: 'Strait of Hormuz — Maritime Chokepoint',
    type: 'tension', lat: 26.6, lon: 56.3, radiusKm: 80,
    severity: 'high', parties: ['Iran IRGCN', 'US 5th Fleet', 'UK Maritime Trade Ops'],
    startDate: '2023-07-01',
    description: 'IRGC Navy vessel seizures and harassment. US/UK naval escorts active. ~21M bbl/day at risk.',
    country: 'Iran',
  },
  {
    id: 'cz-gulf-sa-1', name: 'Saudi Arabia — Houthi Missile/Drone Strikes',
    type: 'active-war', lat: 24.7, lon: 46.7, radiusKm: 150,
    severity: 'high', parties: ['Houthi (Ansar Allah)', 'Saudi Arabia'],
    startDate: '2015-03-26',
    description: 'Ongoing Houthi ballistic missile and drone strikes on Saudi cities, oil infrastructure, and airports.',
    country: 'Saudi Arabia',
  },
  {
    id: 'cz-gulf-us-1', name: 'US CENTCOM — Al Udeid Air Base (Qatar)',
    type: 'tension', lat: 25.12, lon: 51.31, radiusKm: 30,
    severity: 'medium', parties: ['United States', 'Qatar', 'Iran'],
    startDate: '2003-01-01',
    description: 'Major US forward operating base. Over 10,000 troops. Iran has explicitly threatened in escalation scenarios.',
    country: 'Qatar',
  },
  {
    id: 'cz-gulf-bah-1', name: 'US Naval Forces Central Command — Bahrain',
    type: 'tension', lat: 26.19, lon: 50.60, radiusKm: 25,
    severity: 'medium', parties: ['United States', 'Bahrain', 'Iran'],
    startDate: '1995-01-01',
    description: 'HQ of US 5th Fleet. Key target in Iran escalation scenarios. Iranian threats documented.',
    country: 'Bahrain',
  },
  {
    id: 'cz-ir-isr-1', name: 'Iran–Israel Direct Confrontation',
    type: 'active-war', lat: 31.8, lon: 35.2, radiusKm: 100,
    severity: 'critical', parties: ['Iran', 'Israel', 'IRGC', 'IDF'],
    startDate: '2024-04-01',
    description: 'Direct exchange of strikes: Iran ballistic/cruise missile attacks on Israel (Apr & Oct 2024). Israel retaliatory strikes on Iran.',
    country: 'Israel',
  },
  {
    id: 'cz-ir-proxy-1', name: 'Iran Proxy Network — Regional',
    type: 'tension', lat: 33.3, lon: 44.4, radiusKm: 250,
    severity: 'high', parties: ['Iran IRGC-QF', 'Hezbollah', 'Hamas', 'Houthi', 'PMF Iraq'],
    startDate: '2023-10-07',
    description: 'Iran-led "Axis of Resistance" coordinating strikes across Lebanon, Gaza, Iraq, Syria, Yemen targeting US/Israeli assets.',
    country: 'Iraq',
  },
  {
    id: 'cz-oman-1', name: 'Gulf of Oman — Shipping Risk Zone',
    type: 'tension', lat: 22.5, lon: 59.0, radiusKm: 120,
    severity: 'medium', parties: ['Iran IRGCN', 'International Shipping'],
    startDate: '2021-07-29',
    description: 'IRGC tanker seizures and limpet mine incidents in Gulf of Oman. UKMTO alerts active.',
    country: 'Oman',
  },

  // ── Southeast Asia ────────────────────────────────────────────────────────
  {
    id: 'cz-th-kh-1', name: 'Thailand–Cambodia Border Dispute',
    type: 'tension', lat: 14.4, lon: 103.6, radiusKm: 60,
    severity: 'medium', parties: ['Thailand', 'Cambodia'],
    startDate: '2008-07-07',
    description: 'Preah Vihear temple border dispute. Periodic military skirmishes. Maritime EEZ negotiations stalled.',
    country: 'Thailand',
  },
  {
    id: 'cz-ph-npa-1', name: 'Philippines — NPA Communist Insurgency',
    type: 'insurgency', lat: 10.3, lon: 123.9, radiusKm: 200,
    severity: 'medium', parties: ['New People\'s Army (NPA)', 'AFP Philippines'],
    startDate: '1969-03-29',
    description: 'Maoist NPA insurgency across Visayas, Mindanao, and Luzon. Peace talks repeatedly stalled.',
    country: 'Philippines',
  },
  {
    id: 'cz-ph-ms-1', name: 'Philippines — Mindanao (BIFF/Abu Sayyaf)',
    type: 'insurgency', lat: 7.2, lon: 124.2, radiusKm: 150,
    severity: 'high', parties: ['BIFF', 'Abu Sayyaf', 'Maute/ISIS-PH', 'AFP/PNP'],
    startDate: '1991-01-01',
    description: 'ISIS-aligned armed groups in Mindanao and Sulu. Kidnapping and bombings ongoing.',
    country: 'Philippines',
  },
  {
    id: 'cz-mm-wa-1', name: 'Myanmar — Shan State (Brotherhood Alliance)',
    type: 'active-war', lat: 22.5, lon: 99.5, radiusKm: 100,
    severity: 'high', parties: ['UWSA', 'MNDAA', 'TNLA', 'Junta SAC'],
    startDate: '2023-10-27',
    description: 'Op 1027: Brotherhood Alliance seized Laukkaing and hundreds of junta outposts. China-brokered ceasefire contested.',
    country: 'Myanmar',
  },
  {
    id: 'cz-id-papua-1', name: 'Indonesia — West Papua Insurgency',
    type: 'insurgency', lat: -4.5, lon: 136.5, radiusKm: 150,
    severity: 'medium', parties: ['TPNPB (West Papua Liberation)', 'TNI/Polri'],
    startDate: '1965-01-01',
    description: 'Ongoing separatist insurgency in West Papua. Armed attacks on security forces and civilian hostages.',
    country: 'Indonesia',
  },

  // ── South Asia ────────────────────────────────────────────────────────────
  {
    id: 'cz-in-pk-1', name: 'India–Pakistan LoC (Kashmir)',
    type: 'active-war', lat: 34.5, lon: 74.5, radiusKm: 150,
    severity: 'high', parties: ['India', 'Pakistan', 'Lashkar-e-Taiba', 'Jaish-e-Mohammed'],
    startDate: '1947-10-22',
    description: 'Ongoing LoC ceasefire violations and militant infiltrations. Nuclear-armed standoff. 2019 Balakot strikes precedent.',
    country: 'India',
  },
  {
    id: 'cz-in-cn-1', name: 'India–China LAC (Ladakh)',
    type: 'tension', lat: 34.0, lon: 78.5, radiusKm: 200,
    severity: 'high', parties: ['India', 'China (PLA)'],
    startDate: '2020-05-05',
    description: 'LAC standoff since 2020 Galwan clash (20 Indian, 4+ Chinese soldiers killed). PLA infrastructure buildup continues.',
    country: 'India',
  },
  {
    id: 'cz-af-1', name: 'Afghanistan — ISIS-K (ISKP)',
    type: 'insurgency', lat: 33.9, lon: 67.7, radiusKm: 300,
    severity: 'high', parties: ['Islamic Emirate (Taliban)', 'ISIS-Khorasan (ISKP)'],
    startDate: '2021-08-15',
    description: 'ISKP conducting bombings against Taliban and international targets. Planning external attacks (Moscow concert hall 2024).',
    country: 'Afghanistan',
  },
  {
    id: 'cz-bd-1', name: 'Bangladesh — Rohingya / Political Instability',
    type: 'tension', lat: 23.7, lon: 90.4, radiusKm: 100,
    severity: 'medium', parties: ['Interim Bangladesh Govt', 'Islamist factions', 'ARSA (Rohingya)'],
    startDate: '2024-08-05',
    description: 'Post-Hasina transitional instability. Cox\'s Bazar Rohingya camps (1M+) security risk. ARSA armed activity.',
    country: 'Bangladesh',
  },

  // ── Central Asia / Caucasus ───────────────────────────────────────────────
  {
    id: 'cz-az-am-1', name: 'Armenia–Azerbaijan Post-War',
    type: 'tension', lat: 39.8, lon: 46.7, radiusKm: 100,
    severity: 'medium', parties: ['Azerbaijan', 'Armenia'],
    startDate: '2020-09-27',
    description: 'Azerbaijan controls full Karabakh since Sep 2023. 100K+ ethnic Armenians displaced. Border demarcation unresolved.',
    country: 'Azerbaijan',
  },
  {
    id: 'cz-ge-1', name: 'Georgia — South Ossetia / Abkhazia',
    type: 'tension', lat: 42.2, lon: 43.8, radiusKm: 80,
    severity: 'medium', parties: ['Russia', 'Georgia', 'South Ossetia', 'Abkhazia'],
    startDate: '2008-08-08',
    description: 'Russian-occupied Georgian territories. Creeping annexation continuing. Georgia EU membership process under threat.',
    country: 'Georgia',
  },

  // ── Europe ────────────────────────────────────────────────────────────────
  {
    id: 'cz-xk-1', name: 'Kosovo–Serbia Tensions',
    type: 'tension', lat: 42.7, lon: 21.2, radiusKm: 60,
    severity: 'high', parties: ['Serbia', 'Kosovo', 'NATO KFOR', 'Serb north Kosovo'],
    startDate: '2008-02-17',
    description: 'Serbia refuses recognition. North Kosovo flashpoints (Banjska attack 2023). Serbian military on heightened readiness.',
    country: 'Kosovo',
  },
  {
    id: 'cz-ba-1', name: 'Bosnia–Herzegovina (RS Separatism)',
    type: 'tension', lat: 44.5, lon: 17.0, radiusKm: 100,
    severity: 'medium', parties: ['Republika Srpska (Dodik)', 'BiH Federation', 'EU/EUFOR'],
    startDate: '1995-11-21',
    description: 'RS threatening secession and denying Dayton accords. EU/US sanctions on Dodik. EUFOR Althea monitoring.',
    country: 'Bosnia and Herzegovina',
  },
  {
    id: 'cz-md-1', name: 'Moldova — Transnistria',
    type: 'tension', lat: 47.3, lon: 29.0, radiusKm: 50,
    severity: 'medium', parties: ['Russia', 'Transnistria', 'Moldova'],
    startDate: '1990-09-02',
    description: 'Russian-backed frozen conflict. Large Russian arms depot at Cobasna. Transnistria gas crisis 2024.',
    country: 'Moldova',
  },
  {
    id: 'cz-by-1', name: 'Belarus — Wagner / Hybrid Warfare',
    type: 'tension', lat: 53.9, lon: 27.6, radiusKm: 100,
    severity: 'medium', parties: ['Lukashenko', 'Russia', 'Wagner Group remnants', 'NATO border'],
    startDate: '2020-08-09',
    description: 'Wagner Group personnel in Belarus. Migrant weaponization at Polish/Lithuanian borders. Nuclear weapons transfer.',
    country: 'Belarus',
  },
  {
    id: 'cz-cy-1', name: 'Cyprus — EEZ / Turkey Dispute',
    type: 'tension', lat: 35.2, lon: 33.4, radiusKm: 60,
    severity: 'low', parties: ['Cyprus', 'Northern Cyprus (Turkey)'],
    startDate: '1974-07-20',
    description: 'Divided island. Turkey harassing Cyprus gas exploration vessels. Reunification talks failed.',
    country: 'Cyprus',
  },

  // ── Africa ────────────────────────────────────────────────────────────────
  {
    id: 'cz-cd-1', name: 'DR Congo — M23/AFC (East)',
    type: 'active-war', lat: -1.7, lon: 29.2, radiusKm: 150,
    severity: 'critical', parties: ['M23/AFC (Rwanda-backed)', 'FARDC', 'FDLR', 'UN MONUSCO'],
    startDate: '2022-03-01',
    description: 'M23/AFC seized Goma Jan 2025. Advancing towards Bukavu. Rwanda military support documented. 7M+ displaced.',
    country: 'Democratic Republic of the Congo',
  },
  {
    id: 'cz-ly-1', name: 'Libya — East-West Division',
    type: 'tension', lat: 28.0, lon: 19.0, radiusKm: 200,
    severity: 'medium', parties: ['GNU (Tripoli)', 'LNA Haftar', 'Turkey', 'UAE', 'Wagner'],
    startDate: '2014-06-25',
    description: 'Split between western GNU and eastern LNA. Foreign militaries persist. Oil crescent contested.',
    country: 'Libya',
  },
  {
    id: 'cz-ng-1', name: 'Nigeria — Boko Haram / Banditry / IPOB',
    type: 'insurgency', lat: 11.0, lon: 13.0, radiusKm: 300,
    severity: 'high', parties: ['ISWAP', 'Boko Haram', 'Bandits', 'IPOB/ESN'],
    startDate: '2009-07-26',
    description: 'Multi-front insurgency: ISWAP/Boko Haram northeast, bandits northwest, IPOB separatism southeast.',
    country: 'Nigeria',
  },
  {
    id: 'cz-bf-1', name: 'Burkina Faso — JNIM Insurgency',
    type: 'active-war', lat: 13.5, lon: -1.5, radiusKm: 250,
    severity: 'critical', parties: ['JNIM (Al-Qaeda)', 'ISGS', 'Burkina Faso junta', 'Russian instructors'],
    startDate: '2015-01-01',
    description: 'JNIM controls >40% of territory. Ouagadougou supply lines cut. Wagner-linked Africa Corps deployed.',
    country: 'Burkina Faso',
  },
  {
    id: 'cz-mz-1', name: 'Mozambique — Cabo Delgado (ISCAP)',
    type: 'insurgency', lat: -12.0, lon: 40.5, radiusKm: 100,
    severity: 'high', parties: ['ISCAP/Ansar al-Sunna', 'SAMIM', 'Rwandan forces'],
    startDate: '2017-10-05',
    description: 'ISIS-affiliated insurgency targeting gas infrastructure. 700K+ displaced. TOTAL/ENI LNG projects suspended.',
    country: 'Mozambique',
  },
  {
    id: 'cz-ne-1', name: 'Niger — JNIM/Wagner Post-Coup',
    type: 'insurgency', lat: 17.6, lon: 8.1, radiusKm: 200,
    severity: 'high', parties: ['JNIM', 'ISGS', 'Niger junta (CNSP)', 'Wagner/Africa Corps'],
    startDate: '2023-07-26',
    description: 'Coup expelled French/US forces. Wagner arriving. JNIM/ISGS expanding from Mali border.',
    country: 'Niger',
  },
  {
    id: 'cz-cf-1', name: 'Central African Republic (Wagner)',
    type: 'insurgency', lat: 5.5, lon: 18.6, radiusKm: 200,
    severity: 'high', parties: ['CPC rebels', 'FACA', 'Wagner/Africa Corps', 'UN MINUSCA'],
    startDate: '2013-03-24',
    description: 'Wagner controls military operations. CPC rebels remain active. Gold/diamond extraction by Wagner documented.',
    country: 'Central African Republic',
  },
  {
    id: 'cz-td-1', name: 'Chad — Internal Instability',
    type: 'tension', lat: 15.5, lon: 18.7, radiusKm: 150,
    severity: 'medium', parties: ['Transitional military govt', 'FACT rebels', 'Sudan spillover'],
    startDate: '2021-04-20',
    description: 'Post-Déby transition. Sudan war refugees and weapons flowing across border. French Barkhane withdrawal.',
    country: 'Chad',
  },

  // ── Americas ──────────────────────────────────────────────────────────────
  {
    id: 'cz-mx-1', name: 'Mexico — Sinaloa vs CJNG Cartel War',
    type: 'insurgency', lat: 25.0, lon: -107.0, radiusKm: 300,
    severity: 'high', parties: ['Cártel de Sinaloa (Chapitos)', 'CJNG (Mencho)'],
    startDate: '2021-08-01',
    description: 'Cartel fracture war since 2024 Chapitos split. Violence in Sinaloa, Jalisco, Michoacán. Govt losing territory.',
    country: 'Mexico',
  },
  {
    id: 'cz-co-1', name: 'Colombia — ELN / FARC Dissidents',
    type: 'insurgency', lat: 3.5, lon: -74.0, radiusKm: 250,
    severity: 'high', parties: ['ELN', 'FARC-EMC', 'Colombian military'],
    startDate: '2016-11-25',
    description: 'Peace talks with ELN stalled. FARC dissidents expanding. Pacific coast and Amazon under armed group control.',
    country: 'Colombia',
  },
  {
    id: 'cz-ve-gy-1', name: 'Venezuela–Guyana (Essequibo Dispute)',
    type: 'tension', lat: 6.5, lon: -59.0, radiusKm: 150,
    severity: 'high', parties: ['Venezuela (Maduro)', 'Guyana', 'Brazil', 'USA'],
    startDate: '2023-12-01',
    description: 'Venezuela claims 2/3 of Guyana (Essequibo oil-rich region). Dec 2023 referendum. Military posturing on border.',
    country: 'Venezuela',
  },
  {
    id: 'cz-ht-1', name: 'Haiti — Gang Control (Viv Ansanm)',
    type: 'active-war', lat: 18.5, lon: -72.3, radiusKm: 60,
    severity: 'critical', parties: ['Viv Ansanm gang coalition', 'HNP (police)', 'MSS Kenya mission'],
    startDate: '2023-03-01',
    description: 'Armed gangs control 80%+ of Port-au-Prince. State collapse. Kenya-led multinational mission 2024.',
    country: 'Haiti',
  },
  {
    id: 'cz-ec-1', name: 'Ecuador — Narco-Criminal Violence',
    type: 'insurgency', lat: -0.2, lon: -78.5, radiusKm: 100,
    severity: 'high', parties: ['Los Lobos', 'La Nacional', 'Choneros', 'Ecuadorian military'],
    startDate: '2023-07-01',
    description: 'State of emergency. Ecuador declared "internal armed conflict." Prison massacres, targeted killings, TV station takeover.',
    country: 'Ecuador',
  },

  // ── East Asia / Pacific ───────────────────────────────────────────────────
  {
    id: 'cz-ecs-1', name: 'East China Sea — Senkaku/ADIZ',
    type: 'tension', lat: 25.7, lon: 123.5, radiusKm: 120,
    severity: 'high', parties: ['China', 'Japan', 'Taiwan', 'US 7th Fleet'],
    startDate: '2013-11-23',
    description: 'China ADIZ overlaps Japanese/Taiwanese airspace. Daily PLA Air Force incursions near Senkaku/Diaoyutai islands.',
    country: 'Japan',
  },
  {
    id: 'cz-pg-1', name: 'Papua New Guinea — Highland Tribal War',
    type: 'insurgency', lat: -6.3, lon: 143.9, radiusKm: 150,
    severity: 'medium', parties: ['Tribal factions (Highlands)', 'PNG Defence Force'],
    startDate: '2022-01-01',
    description: 'Armed tribal warfare with modern weapons in Highlands. 2024 Enga province massacre: 50+ killed in single attack.',
    country: 'Papua New Guinea',
  },
  {
    id: 'cz-sl-1', name: 'Solomon Islands — China Military Pact',
    type: 'tension', lat: -9.6, lon: 160.2, radiusKm: 50,
    severity: 'medium', parties: ['Solomon Islands', 'China', 'Australia', 'USA'],
    startDate: '2022-04-01',
    description: 'China-Solomon Islands security pact. Potential Chinese naval base. Australia/US counter-influence competition.',
    country: 'Solomon Islands',
  },
];

// ---------------------------------------------------------------------------
// Drone Zones
// ---------------------------------------------------------------------------

export const DRONE_ZONES: DroneZone[] = [
  { id: 'dz-ua-1', name: 'Ukraine FPV Drone Combat', lat: 48.5, lon: 37.0, radiusKm: 150, type: 'active-combat', country: 'Ukraine', severity: 'critical' },
  { id: 'dz-ua-2', name: 'Ukraine Long-Range Strike Drones', lat: 55.0, lon: 37.6, radiusKm: 80, type: 'active-combat', country: 'Russia', severity: 'high' },
  { id: 'dz-gz-1', name: 'Gaza Drone Operations', lat: 31.4, lon: 34.4, radiusKm: 30, type: 'active-combat', severity: 'critical' },
  { id: 'dz-ye-1', name: 'Houthi Drone/Missile Corridor', lat: 20.0, lon: 43.0, radiusKm: 200, type: 'surveillance', country: 'Yemen', severity: 'high' },
  { id: 'dz-sy-1', name: 'Syria/Iraq US Drone Ops', lat: 34.5, lon: 40.0, radiusKm: 150, type: 'surveillance', severity: 'medium' },
  { id: 'dz-scs-1', name: 'South China Sea MALE Drones', lat: 16.0, lon: 115.0, radiusKm: 200, type: 'patrol', country: 'China', severity: 'medium' },
  { id: 'dz-tw-1', name: 'Taiwan Strait UAV Patrols', lat: 24.5, lon: 119.5, radiusKm: 100, type: 'patrol', country: 'China', severity: 'high' },
  { id: 'dz-kp-1', name: 'Korean Peninsula Surveillance', lat: 38.5, lon: 127.5, radiusKm: 100, type: 'surveillance', country: 'North Korea', severity: 'medium' },
  { id: 'dz-ir-1', name: 'Iran Shaheed Drone Corridor', lat: 29.0, lon: 52.0, radiusKm: 150, type: 'active-combat', country: 'Iran', severity: 'high' },
  { id: 'dz-mm-1', name: 'Myanmar Military Drone Strikes', lat: 19.5, lon: 96.0, radiusKm: 120, type: 'active-combat', country: 'Myanmar', severity: 'high' },
  { id: 'dz-gulf-1', name: 'Iran Shahed Drone Threat — Gulf', lat: 26.0, lon: 54.5, radiusKm: 300, type: 'active-combat', country: 'Iran', severity: 'critical' },
  { id: 'dz-gulf-2', name: 'Houthi Anti-Ship Missile Zone', lat: 14.5, lon: 43.0, radiusKm: 250, type: 'active-combat', country: 'Yemen', severity: 'high' },
  { id: 'dz-uae-1', name: 'UAE Air Defense Zone', lat: 24.5, lon: 54.4, radiusKm: 80, type: 'surveillance', country: 'United Arab Emirates', severity: 'high' },
];

// ---------------------------------------------------------------------------
// Nuclear Assets — OSINT data from Nuclear Threat Initiative, FAS, etc.
// ---------------------------------------------------------------------------

export const NUCLEAR_ASSETS: NuclearAsset[] = [
  // USA
  { id: 'nuc-us-1', name: 'Minot AFB (Minuteman III)', country: 'United States', countryCode: 'US', lat: 48.42, lon: -101.36, type: 'icbm-silo', rangeKm: 13000 },
  { id: 'nuc-us-2', name: 'F.E. Warren AFB', country: 'United States', countryCode: 'US', lat: 41.12, lon: -104.82, type: 'icbm-silo', rangeKm: 13000 },
  { id: 'nuc-us-3', name: 'Malstrom AFB (Montana)', country: 'United States', countryCode: 'US', lat: 47.51, lon: -111.19, type: 'icbm-silo', rangeKm: 13000 },
  { id: 'nuc-us-4', name: 'Kitsap-Bangor (SSBN Base)', country: 'United States', countryCode: 'US', lat: 47.72, lon: -122.70, type: 'submarine-base', rangeKm: 12000 },
  { id: 'nuc-us-5', name: 'Barksdale AFB (B-52)', country: 'United States', countryCode: 'US', lat: 32.50, lon: -93.66, type: 'air-base', rangeKm: 16000 },
  { id: 'nuc-us-6', name: 'Whiteman AFB (B-21/B-2)', country: 'United States', countryCode: 'US', lat: 38.72, lon: -93.55, type: 'air-base', rangeKm: 12000 },
  // Russia
  { id: 'nuc-ru-1', name: 'Kozelsk ICBM Silo (Topol-M)', country: 'Russia', countryCode: 'RU', lat: 54.03, lon: 35.78, type: 'icbm-silo', rangeKm: 11000 },
  { id: 'nuc-ru-2', name: 'Tatishchevo ICBM Base', country: 'Russia', countryCode: 'RU', lat: 51.68, lon: 45.95, type: 'icbm-silo', rangeKm: 11000 },
  { id: 'nuc-ru-3', name: 'Yurya ICBM Base', country: 'Russia', countryCode: 'RU', lat: 59.02, lon: 49.25, type: 'icbm-silo', rangeKm: 11000 },
  { id: 'nuc-ru-4', name: 'Gadzhiyevo Sub Base (Delta IV)', country: 'Russia', countryCode: 'RU', lat: 69.25, lon: 33.32, type: 'submarine-base', rangeKm: 8300 },
  { id: 'nuc-ru-5', name: 'Vilyuchinsk Sub Base (Pacific)', country: 'Russia', countryCode: 'RU', lat: 52.93, lon: 158.40, type: 'submarine-base', rangeKm: 8300 },
  { id: 'nuc-ru-6', name: 'Engels-2 AFB (Tu-160)', country: 'Russia', countryCode: 'RU', lat: 51.49, lon: 46.17, type: 'air-base', rangeKm: 12000 },
  // China
  { id: 'nuc-cn-1', name: 'Jilantai ICBM Training Area', country: 'China', countryCode: 'CN', lat: 39.25, lon: 105.58, type: 'icbm-silo', rangeKm: 14000 },
  { id: 'nuc-cn-2', name: 'Gansu DF-5 Silos', country: 'China', countryCode: 'CN', lat: 40.0, lon: 97.0, type: 'icbm-silo', rangeKm: 13000 },
  { id: 'nuc-cn-3', name: 'Yulin Sub Base (Jin-class)', country: 'China', countryCode: 'CN', lat: 18.23, lon: 109.60, type: 'submarine-base', rangeKm: 8000 },
  // UK
  { id: 'nuc-gb-1', name: 'HMNB Clyde (Trident)', country: 'United Kingdom', countryCode: 'GB', lat: 56.04, lon: -4.82, type: 'submarine-base', rangeKm: 12000 },
  // France
  { id: 'nuc-fr-1', name: 'Île Longue (SSBN Base)', country: 'France', countryCode: 'FR', lat: 48.35, lon: -4.57, type: 'submarine-base', rangeKm: 11000 },
  // India
  { id: 'nuc-in-1', name: 'Kalaikunda AFB (Nuclear-capable)', country: 'India', countryCode: 'IN', lat: 22.33, lon: 87.20, type: 'air-base' },
  { id: 'nuc-in-2', name: 'INS Arihant Base', country: 'India', countryCode: 'IN', lat: 17.69, lon: 83.22, type: 'submarine-base', rangeKm: 3500 },
  // Pakistan
  { id: 'nuc-pk-1', name: 'Kamra/Attock AFB', country: 'Pakistan', countryCode: 'PK', lat: 33.87, lon: 72.40, type: 'air-base' },
  { id: 'nuc-pk-2', name: 'Akro Missile Storage', country: 'Pakistan', countryCode: 'PK', lat: 33.12, lon: 73.58, type: 'storage' },
  // North Korea
  { id: 'nuc-kp-1', name: 'Punggye-ri Test Site', country: 'North Korea', countryCode: 'KP', lat: 41.29, lon: 129.10, type: 'test-site' },
  { id: 'nuc-kp-2', name: 'Sohae Satellite Launch', country: 'North Korea', countryCode: 'KP', lat: 39.66, lon: 124.70, type: 'air-base', rangeKm: 15000 },
  { id: 'nuc-kp-3', name: 'Yongbyon Nuclear Complex', country: 'North Korea', countryCode: 'KP', lat: 39.79, lon: 125.75, type: 'storage' },
  // Israel
  { id: 'nuc-il-1', name: 'Negev Nuclear Research Center', country: 'Israel', countryCode: 'IL', lat: 31.00, lon: 34.98, type: 'storage' },
];

// ---------------------------------------------------------------------------
// Fighter Jets — near conflict zones + air patrols (OSINT simulation)
// ---------------------------------------------------------------------------

const JET_PATTERNS: Omit<FighterJet, 'id'>[] = [
  // Ukraine theater
  { callsign: 'UAF-21', aircraft: 'F-16A', lat: 49.5, lon: 30.0, heading: 90, altitude: 8000, country: 'Ukraine', countryCode: 'UA', mission: 'patrol' },
  { callsign: 'UAF-22', aircraft: 'F-16A', lat: 48.5, lon: 33.0, heading: 270, altitude: 7500, country: 'Ukraine', countryCode: 'UA', mission: 'combat' },
  { callsign: 'VVS-311', aircraft: 'Su-35S', lat: 51.5, lon: 36.0, heading: 180, altitude: 9000, country: 'Russia', countryCode: 'RU', mission: 'combat' },
  { callsign: 'VVS-312', aircraft: 'Su-57', lat: 52.0, lon: 40.0, heading: 270, altitude: 12000, country: 'Russia', countryCode: 'RU', mission: 'patrol' },
  // Taiwan Strait
  { callsign: 'ROCAF-1', aircraft: 'F-16V', lat: 24.0, lon: 121.5, heading: 270, altitude: 8500, country: 'Taiwan', countryCode: 'TW', mission: 'intercept' },
  { callsign: 'PLAAF-1', aircraft: 'J-20', lat: 25.0, lon: 119.0, heading: 90, altitude: 11000, country: 'China', countryCode: 'CN', mission: 'patrol' },
  { callsign: 'PLAAF-2', aircraft: 'J-16', lat: 23.5, lon: 120.5, heading: 180, altitude: 9000, country: 'China', countryCode: 'CN', mission: 'patrol' },
  // Korean Peninsula
  { callsign: 'ROKAF-1', aircraft: 'F-35A', lat: 37.3, lon: 127.5, heading: 0, altitude: 9000, country: 'South Korea', countryCode: 'KR', mission: 'patrol' },
  { callsign: 'JASDF-1', aircraft: 'F-35A', lat: 34.0, lon: 131.0, heading: 0, altitude: 8000, country: 'Japan', countryCode: 'JP', mission: 'patrol' },
  { callsign: 'USAF-1', aircraft: 'F-22A', lat: 36.5, lon: 128.0, heading: 270, altitude: 13000, country: 'United States', countryCode: 'US', mission: 'patrol' },
  { callsign: 'KPAAF-1', aircraft: 'MiG-29', lat: 39.5, lon: 126.0, heading: 180, altitude: 7000, country: 'North Korea', countryCode: 'KP', mission: 'patrol' },
  // Middle East
  { callsign: 'IAF-1', aircraft: 'F-35I Adir', lat: 32.0, lon: 34.8, heading: 90, altitude: 10000, country: 'Israel', countryCode: 'IL', mission: 'patrol' },
  { callsign: 'USAF-2', aircraft: 'F-15E', lat: 26.0, lon: 50.0, heading: 270, altitude: 9000, country: 'United States', countryCode: 'US', mission: 'patrol' },
  // NATO Baltic / Eastern Europe
  { callsign: 'NATO-1', aircraft: 'EF-2000', lat: 59.5, lon: 24.5, heading: 90, altitude: 9000, country: 'United Kingdom', countryCode: 'GB', mission: 'patrol' },
  { callsign: 'NATO-2', aircraft: 'F-16C', lat: 54.0, lon: 18.0, heading: 270, altitude: 8500, country: 'Poland', countryCode: 'PL', mission: 'patrol' },
  // South China Sea
  { callsign: 'USAF-3', aircraft: 'B-52H', lat: 14.0, lon: 130.0, heading: 270, altitude: 11500, country: 'United States', countryCode: 'US', mission: 'patrol' },
  { callsign: 'PLAAF-3', aircraft: 'H-6K', lat: 18.0, lon: 116.0, heading: 180, altitude: 9500, country: 'China', countryCode: 'CN', mission: 'patrol' },
];

let jetCache: FighterJet[] | null = null;
let jetCacheTime = 0;

export function fetchFighterJets(): FighterJet[] {
  const now = Date.now();
  if (jetCache && now - jetCacheTime < 5 * 60 * 1000) return jetCache;

  jetCache = JET_PATTERNS.map((j, i) => ({
    ...j,
    id: `jet-${i}`,
    lat: j.lat + Math.sin(now / 1800000 + i * 1.7) * 0.4,
    lon: j.lon + Math.cos(now / 1800000 + i * 1.7) * 0.4,
    heading: (j.heading + Math.sin(now / 900000 + i) * 20 + 360) % 360,
  }));
  jetCacheTime = now;
  return jetCache;
}
